import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../lib/api';
import { Id } from '../../convex/_generated/dataModel';

interface User {
  _id: Id<"players">;
  name: string;
  firstName?: string;
  lastName?: string;
  phoneNumber: string;
  role?: "player" | "organizer" | "administrator";
  profilePicture?: string;
  waitList?: boolean;
  isVerified: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (phoneNumber: string, password: string) => Promise<void>;
  signUp: (name: string, phoneNumber: string, password: string) => Promise<{ playerId: string }>;
  signOut: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [playerId, setPlayerId] = useState<Id<"players"> | null>(() => {
    const stored = localStorage.getItem('playerId');
    return stored ? stored as Id<"players"> : null;
  });
  const [loading, setLoading] = useState(true);

  const signInMutation = useMutation(api.auth.signIn);
  const signUpMutation = useMutation(api.auth.signUp);

  const user = useQuery(
    api.auth.getCurrentUser,
    playerId ? { playerId } : "skip"
  ) as User | null;

  useEffect(() => {
    if (playerId) {
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [playerId, user]);

  const signIn = async (phoneNumber: string, password: string) => {
    const result = await signInMutation({
      phoneNumber,
      password,
    });

    localStorage.setItem('playerId', result.playerId);
    setPlayerId(result.playerId as Id<"players">);
  };

  const signUp = async (name: string, phoneNumber: string, password: string) => {
    const result = await signUpMutation({
      name,
      phoneNumber,
      password,
    });

    localStorage.setItem('playerId', result.playerId);
    setPlayerId(result.playerId as Id<"players">);
    return result;
  };

  const signOut = () => {
    localStorage.removeItem('playerId');
    setPlayerId(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signIn,
        signUp,
        signOut,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}